import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Lock,
  LockOpen,
  Calculate,
  Sports,
  People,
  Assignment,
  CheckCircle,
  Cancel,
  Pending,
  EmojiEvents,
  Refresh
} from '@mui/icons-material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
import { adminAPI } from '../services/api';
import { getSpreadDisplay } from '../utils/gameUtils';

interface AdminDashboardData {
  currentWeek: {
    id: number;
    week_number: number;
    year: number;
    picks_lock_time: string;
    total_games: number;
    completed_games: number;
  } | null;
  picksLocked: boolean;
  totalUsers: number;
  totalPicks: number;
  correctPicks: number;
  incorrectPicks: number;
  pendingPicks: number;
}

interface GameData {
  id: number;
  home_team: string;
  away_team: string;
  spread?: number;
  game_time: string;
  home_score?: number;
  away_score?: number;
  is_final: boolean;
  is_spread_pick: boolean;
}

interface CurrentWeekData {
  week: {
    id: number;
    week_number: number;
    year: number;
  };
  games: GameData[];
}

const Admin: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<AdminDashboardData | null>(null);
  const [currentWeekGames, setCurrentWeekGames] = useState<CurrentWeekData | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadDashboardData = async () => {
    try {
      const data = await adminAPI.getDashboard();
      setDashboardData(data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      showSnackbar('Failed to load dashboard data', 'error');
    }
  };

  const loadCurrentWeekGames = async () => {
    try {
      const data = await adminAPI.getCurrentWeekGames();
      setCurrentWeekGames(data);
    } catch (error) {
      console.error('Failed to load current week games:', error);
      showSnackbar('Failed to load games data', 'error');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadDashboardData(), loadCurrentWeekGames()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleLockPicks = async () => {
    setActionLoading('lock');
    try {
      await adminAPI.lockPicks();
      showSnackbar('Picks locked successfully', 'success');
      await loadDashboardData();
    } catch (error) {
      console.error('Failed to lock picks:', error);
      showSnackbar('Failed to lock picks', 'error');
    }
    setActionLoading(null);
  };

  const handleUnlockPicks = async () => {
    setActionLoading('unlock');
    try {
      await adminAPI.unlockPicks();
      showSnackbar('Picks unlocked successfully', 'success');
      await loadDashboardData();
    } catch (error) {
      console.error('Failed to unlock picks:', error);
      showSnackbar('Failed to unlock picks', 'error');
    }
    setActionLoading(null);
  };

  const handleUpdateGameScore = async (gameId: number, homeScore: number, awayScore: number, isFinal: boolean) => {
    setActionLoading(`game-${gameId}`);
    try {
      await adminAPI.updateGameScores(gameId, homeScore, awayScore, isFinal);
      showSnackbar('Game scores updated successfully', 'success');
      await loadCurrentWeekGames();
    } catch (error) {
      console.error('Failed to update game scores:', error);
      showSnackbar('Failed to update game scores', 'error');
    }
    setActionLoading(null);
  };

  const handleCalculateResults = async () => {
    setActionLoading('calculate');
    try {
      const result = await adminAPI.calculateResults();
      showSnackbar(`Results calculated: ${result.results.picksCalculated} picks processed, ${result.results.correctPicks} correct`, 'success');
      await Promise.all([loadDashboardData(), loadCurrentWeekGames()]);
    } catch (error) {
      console.error('Failed to calculate results:', error);
      showSnackbar('Failed to calculate results', 'error');
    }
    setActionLoading(null);
  };

  const handleUpdateRankings = async () => {
    setActionLoading('rankings');
    try {
      const result = await adminAPI.updateRankings();
      showSnackbar(`Rankings updated: ${result.message}`, 'success');
    } catch (error) {
      console.error('Error updating rankings:', error);
      showSnackbar('Failed to update rankings', 'error');
    }
    setActionLoading(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!dashboardData?.currentWeek) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Admin Dashboard
        </Typography>
        <Alert severity="info">
          No active week found. Create a new week to access admin functions.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Admin Dashboard
      </Typography>
      
      {/* Current Week Overview */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Current Week: Week {dashboardData.currentWeek.week_number} {dashboardData.currentWeek.year}
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={2}>
            <Box flex="1" minWidth="200px">
              <Box display="flex" alignItems="center" gap={1}>
                <People color="primary" />
                <Typography variant="body2">
                  <strong>{dashboardData.totalUsers}</strong> Active Users
                </Typography>
              </Box>
            </Box>
            <Box flex="1" minWidth="200px">
              <Box display="flex" alignItems="center" gap={1}>
                <Sports color="primary" />
                <Typography variant="body2">
                  <strong>{dashboardData.currentWeek.completed_games}/{dashboardData.currentWeek.total_games}</strong> Games Complete
                </Typography>
              </Box>
            </Box>
            <Box flex="1" minWidth="200px">
              <Box display="flex" alignItems="center" gap={1}>
                <Assignment color="primary" />
                <Typography variant="body2">
                  <strong>{dashboardData.totalPicks}</strong> Total Picks
                </Typography>
              </Box>
            </Box>
            <Box flex="1" minWidth="200px">
              <Box display="flex" alignItems="center" gap={1}>
                {dashboardData.picksLocked ? <Lock color="error" /> : <LockOpen color="success" />}
                <Typography variant="body2">
                  Picks {dashboardData.picksLocked ? 'Locked' : 'Open'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Pick Statistics */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Pick Statistics
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={2}>
            <Box flex="1" minWidth="150px">
              <Box display="flex" alignItems="center" gap={1}>
                <CheckCircle color="success" />
                <Typography variant="body2">
                  <strong>{dashboardData.correctPicks}</strong> Correct
                </Typography>
              </Box>
            </Box>
            <Box flex="1" minWidth="150px">
              <Box display="flex" alignItems="center" gap={1}>
                <Cancel color="error" />
                <Typography variant="body2">
                  <strong>{dashboardData.incorrectPicks}</strong> Incorrect
                </Typography>
              </Box>
            </Box>
            <Box flex="1" minWidth="150px">
              <Box display="flex" alignItems="center" gap={1}>
                <Pending color="warning" />
                <Typography variant="body2">
                  <strong>{dashboardData.pendingPicks}</strong> Pending
                </Typography>
              </Box>
            </Box>
            <Box flex="1" minWidth="150px">
              <Typography variant="body2">
                Accuracy: <strong>
                  {dashboardData.totalPicks > 0 
                    ? Math.round((dashboardData.correctPicks / (dashboardData.correctPicks + dashboardData.incorrectPicks)) * 100) || 0
                    : 0}%
                </strong>
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Admin Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Admin Actions
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={2}>
            <Box flex="1" minWidth="200px">
              <Button
                variant="contained"
                color={dashboardData.picksLocked ? "success" : "error"}
                startIcon={dashboardData.picksLocked ? <LockOpen /> : <Lock />}
                onClick={dashboardData.picksLocked ? handleUnlockPicks : handleLockPicks}
                disabled={actionLoading === 'lock' || actionLoading === 'unlock'}
                fullWidth
              >
                {actionLoading === 'lock' || actionLoading === 'unlock' ? (
                  <CircularProgress size={20} />
                ) : (
                  dashboardData.picksLocked ? 'Unlock Picks' : 'Lock Picks'
                )}
              </Button>
            </Box>
            <Box flex="1" minWidth="200px">
              <Button
                variant="contained"
                color="primary"
                startIcon={<Calculate />}
                onClick={handleCalculateResults}
                disabled={actionLoading === 'calculate'}
                fullWidth
              >
                {actionLoading === 'calculate' ? (
                  <CircularProgress size={20} />
                ) : (
                  'Calculate Results'
                )}
              </Button>
            </Box>
            <Box flex="1" minWidth="200px">
              <Button
                variant="contained"
                color="secondary"
                startIcon={<EmojiEvents />}
                onClick={handleUpdateRankings}
                disabled={actionLoading === 'rankings'}
                fullWidth
              >
                {actionLoading === 'rankings' ? (
                  <CircularProgress size={20} />
                ) : (
                  'Update Rankings'
                )}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Game Score Management */}
      {currentWeekGames && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Game Score Management - Week {currentWeekGames.week.week_number} {currentWeekGames.week.year}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Game</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Spread</TableCell>
                    <TableCell>Home Score</TableCell>
                    <TableCell>Away Score</TableCell>
                    <TableCell>Final</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentWeekGames.games.map((game) => (
                    <GameScoreRow
                      key={game.id}
                      game={game}
                      onUpdate={handleUpdateGameScore}
                      loading={actionLoading === `game-${game.id}`}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

interface GameScoreRowProps {
  game: GameData;
  onUpdate: (gameId: number, homeScore: number, awayScore: number, isFinal: boolean) => void;
  loading: boolean;
}

const GameScoreRow: React.FC<GameScoreRowProps> = ({ game, onUpdate, loading }) => {
  const [homeScore, setHomeScore] = useState(game.home_score?.toString() || '');
  const [awayScore, setAwayScore] = useState(game.away_score?.toString() || '');
  const [isFinal, setIsFinal] = useState(game.is_final);

  const handleUpdate = () => {
    const home = parseInt(homeScore) || 0;
    const away = parseInt(awayScore) || 0;
    onUpdate(game.id, home, away, isFinal);
  };

  const hasChanges = 
    homeScore !== (game.home_score?.toString() || '') ||
    awayScore !== (game.away_score?.toString() || '') ||
    isFinal !== game.is_final;

  return (
    <TableRow>
      <TableCell>
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {game.away_team} @ {game.home_team}
          </Typography>
          {game.is_spread_pick && (
            <Chip 
              label="Spread Pick" 
              size="small" 
              color="primary" 
              variant="outlined"
            />
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {(() => {
            let date = dayjs(game.game_time);
            if (!game.game_time.includes('Z') && !game.game_time.includes('+') && !game.game_time.includes('-')) {
              date = dayjs.utc(game.game_time);
            }
            return date.tz('America/New_York').format('ddd, MMM D @ h:mm A z');
          })()}
        </Typography>
      </TableCell>
      <TableCell>
        {game.spread ? (
          <Chip 
            label={getSpreadDisplay(game)}
            size="small" 
            color="info"
            variant="outlined"
            sx={{ 
              fontSize: '0.7rem', 
              fontWeight: 600,
              bgcolor: 'info.light',
              color: 'info.contrastText'
            }} 
          />
        ) : 'N/A'}
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          type="number"
          value={homeScore}
          onChange={(e) => setHomeScore(e.target.value)}
          sx={{ width: 80 }}
          inputProps={{ min: 0 }}
        />
      </TableCell>
      <TableCell>
        <TextField
          size="small"
          type="number"
          value={awayScore}
          onChange={(e) => setAwayScore(e.target.value)}
          sx={{ width: 80 }}
          inputProps={{ min: 0 }}
        />
      </TableCell>
      <TableCell>
        <FormControlLabel
          control={
            <Switch
              checked={isFinal}
              onChange={(e) => setIsFinal(e.target.checked)}
              size="small"
            />
          }
          label="Final"
        />
      </TableCell>
      <TableCell>
        <Button
          size="small"
          variant="contained"
          onClick={handleUpdate}
          disabled={!hasChanges || loading}
        >
          {loading ? <CircularProgress size={16} /> : 'Update'}
        </Button>
      </TableCell>
    </TableRow>
  );
};

export default Admin;
